import type { CSSProperties } from 'react';
import classNames from 'classnames';
import { Button, Chip, Tooltip } from '@servicetitan/anvil2';
import { core } from '@servicetitan/anvil2/token';
import CloseIcon from '@servicetitan/anvil2/assets/icons/material/round/close.svg';
import { Divider } from '@servicetitan/design-system';
import { inboxLocators } from '@locators/inbox';
import noop from '@lib/utils/noop';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { resolveTenantName } from '@modules/AuthModule/lib/resolveOrgNodeId';
import { manualBookingName } from '@modules/InboxModule/lib/manualBookings';
import type { ITask } from '@modules/TasksModule/types';
import useManualBookingInProgress from './useManualBookingInProgress';
import { closeManualBooking } from './closeManualBooking';
import styles from './ManualBookingCard.module.css';

/** What a manual booking card is called until a customer is picked. */
type Props = {
  task: ITask;
  active: boolean;
  onClick?: () => void;
  divider?: boolean;
};

/**
 * A manual booking card in Assigned to Me. It shows "New booking" until a
 * customer is picked, then the customer's name and tenant, and an "In progress"
 * chip once a booking has started. It has no source line and no timer. The
 * card's content is a native button, which selects the card; the X beside it
 * closes the card.
 */
export const ManualBookingCard = ({
  task,
  active,
  onClick = noop,
  divider = true,
}: Props) => {
  const { user } = useAuth();
  const isBookingInProgress = useManualBookingInProgress(task);
  const hasCustomer = !!task.attributes.customerId;
  const name = manualBookingName(task);
  const tenantName = hasCustomer
    ? resolveTenantName(
        user?.org.tenants ?? [],
        task.attributes.customerOrgNodeId,
      )
    : undefined;

  const handleClose = () => {
    void closeManualBooking(task.id);
  };

  return (
    <>
      <div
        className={classNames(styles.card, { [styles.active]: active })}
        data-testid={inboxLocators.taskList.manualBookingCard}
        data-task-id={task.id}
      >
        <button
          type='button'
          className={styles.select}
          aria-current={active ? 'true' : undefined}
          data-testid={inboxLocators.taskList.manualBookingSelectButton}
          onClick={onClick}
        >
          <span
            className={styles.name}
            title={name}
            data-testid={inboxLocators.taskList.manualBookingName}
          >
            {name}
          </span>
          {tenantName && (
            <span
              className={styles.tenant}
              data-testid={inboxLocators.taskList.manualBookingTenant}
            >
              {tenantName}
            </span>
          )}
          {isBookingInProgress && (
            <Chip
              label='In progress'
              size='small'
              color={core.primitive.ColorYellow100.value}
              // Chip darkens the border it derives from `color`; the design
              // keeps it Yellow 400
              style={
                {
                  '--a2-inline-border-color':
                    core.primitive.ColorYellow400.value,
                } as CSSProperties
              }
              data-testid={inboxLocators.taskList.manualBookingInProgress}
            />
          )}
        </button>
        {/* an icon-only Button gets a tooltip with its label, as Anvil2 asks */}
        <Tooltip placement='top'>
          <Tooltip.Trigger className={styles.close}>
            <Button
              appearance='ghost'
              size='small'
              icon={CloseIcon}
              aria-label={`Close ${name}`}
              onClick={handleClose}
              data-testid={inboxLocators.taskList.manualBookingCloseButton}
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{`Close ${name}`}</Tooltip.Content>
        </Tooltip>
      </div>
      {divider && <Divider spacing='0' />}
    </>
  );
};
